<template>
  <v-card flat class="asset_review_step">
    <v-card-title class="pa-0">
      <h4>
        {{ campaign.AccessReviewTitle }}
      </h4>
    </v-card-title>
    <v-card-text
      v-if="accessList.length === 0 && !allowAddingAccess"
      key="no-access-to-review"
      class="px-0 pb-3"
    >
      <p class="mb-0">This asset has no access to review</p>
    </v-card-text>
    <v-card-text
      v-else
      id="accessReviewCard"
      key="has-access-to-review"
      class="pa-0"
    >
      <p class="mb-1">
        {{ campaign.AccessReviewDesc }}
      </p>
      <div id="accessList" class="pb-2">
        <div class="access-Table">
          <status-display-bar :error="errorMessage" :loading="isLoading" />
          <sphere-table
            ref="accessTable"
            client-side
            table-type="actionable"
            name="AccessTable"
            :data-items="accessListItems"
            :columns="accessReviewColumns[assetType.name]"
            :configurable-table-key="`asset-review-step-access-${assetType.name}`"
            @selectionchange="keepRetireSelectionChange"
          >
            <template #no-data> This asset has no access to review </template>

            <template v-slot:warningTemplate="{ props }">
              <td :class="props.field.class">
                <div
                  v-if="
                    !accessLevels.includes(props.dataItem.value) &&
                    props.dataItem.value != null
                  "
                  key="invalid-access-level"
                  class="warning--text"
                >
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon small color="warning" v-bind="attrs" v-on="on">
                        {{ SbIcons.status.warn }}
                      </v-icon>
                      {{ props.dataItem.value }}
                    </template>
                    <span
                      >Modify and Read Only are the only acceptable values.
                      Please choose one.</span
                    >
                  </v-tooltip>
                </div>
                <div v-else key="valid-access-level">
                  {{ props.dataItem.value }}
                </div>
              </td>
            </template>
            <template
              v-if="!readOnlyAccessReview"
              v-slot:keepRetireTemplate="{ props }"
            >
              <td :class="props.field.class">
                <v-btn-toggle
                  :key="props.dataItem.stsGuid + props.dataItem.newValue"
                  v-model="props.dataItem.newValue"
                  mandatory
                  class="my-1"
                  @click.stop
                >
                  <v-btn
                    v-for="(val, idx) in accessLevels"
                    :id="`accessLevel_${idx}`"
                    :key="idx"
                    :value="val"
                    small
                    class="sb_btn_solid"
                    :title="accessTooltips[val]"
                    @click="updateAccess([props.dataItem], val)"
                  >
                    {{ val }}
                  </v-btn>
                </v-btn-toggle>
              </td>
            </template>

            <template v-slot:actionBtns>
              <v-layout wrap row text-xs-right class="full_width">
                <v-flex lg12>
                  <span v-if="!readOnlyAccessReview">
                    <v-btn
                      v-for="(accessLevel, idx) in accessLevels"
                      :id="`AccessLevel_${idx}`"
                      :key="idx"
                      class="sb_btn_solid"
                      outline
                      :color="accessLevel == 'Remove' ? 'error' : ''"
                      :title="accessTooltips[accessLevel]"
                      :disabled="
                        selectedItems.length === 0 || readOnlyAccessReview
                      "
                      @click="updateSelectedAccess(selectedItems, accessLevel)"
                    >
                      {{ accessLevel }}
                    </v-btn>
                  </span>
                  <!--FIXME:
      only allow adding a user once, maybe use a toast to notify if
      that's why the user wasn't added to the list (a second time)
    -->
                  <user-select-dialog
                    v-if="allowAddingAccess"
                    id="AddAccessBtn"
                    :disabled="selectedItems.length > 0 || readOnlyAccessReview"
                    button-text="Add Access"
                    button-color="primary"
                    button-icon="add"
                    body-text="To grant access to addition users, select them here to add them to the list, then select the level of access they should have"
                    require-selection
                    asset-review-step
                    @selected-users="addAccessForUsers"
                  />
                </v-flex>
              </v-layout>
            </template>
            <template v-slot:accessChangeReason="{ props }">
              <td :class="props.field.class">
                <v-text-field
                  v-model="props.dataItem.reason"
                  :disabled="!props.dataItem.isTextFieldEnabled"
                  class="my-1"
                  maxlength="100"
                  :rules="
                    props.dataItem.isTextFieldEnabled && validateRules
                      ? rules
                      : []
                  "
                  counter
                  small
                  dense
                  @input="UpdateRemoveReason([props.dataItem])"
                />
              </td>
            </template>
          </sphere-table>
        </div>
      </div>
    </v-card-text>
    <v-snackbar v-model="snackbar" top color="warning">
      <v-icon left>
        {{ SbIcons.status.warn }}
      </v-icon>
      {{ snackbarMessage }}
    </v-snackbar>
  </v-card>
</template>

<script>
/**
 * In this review step, the owner can make changes to the level of access that
 * other assets have to the asset being reviewed (e.g. permissions on a share,
 * membership in a group)
 */
import { fetch } from '../../../utility/fetch.js';
import StatusDisplayBar from '../../StatusDisplayBar.vue';
import UserSelectDialog from '../../UserSelectDialog.vue';
import reviewStepBase from './reviewStepBase.mixin.js';
import SphereTable from '../../../components/Table/SphereTable.vue';

export default {
  name: 'AssetReviewStepAccess',
  components: {
    SphereTable,
    StatusDisplayBar,
    UserSelectDialog,
  },
  mixins: [reviewStepBase],
  props: {
    resetSelectedAccess: {
      type: Boolean,
    },
    startValidate: {
      type: Boolean,
    },
    resetWidth: {
      type: Boolean,
    },
  },
  data() {
    return {
      errorMessage: undefined,
      snackbar: false,
      snackbarMessage: undefined,
      isLoading: false,
      selectedItems: [],
      selected: [],
      accessListItems: [],
      reviewPage: [],
      validateRules: false,
      rules: [(v) => !!v || 'Required'],
    };
  },
  computed: {
    allowAddingAccess() {
      return this.configureSteps.AddAccess;
    },
    campaign() {
      return this.$store.state.campaignDetails.overview;
    },
  },
  watch: {
    startValidate() {
      this.validateReviewStepAccess();
    },
    // Reset column widths after the table is visible
    resetWidth() {
      setTimeout(() => {
        this.$nextTick(() => {
          this.$refs.accessTable.resetAllColumnsWidth();
        });
      }, 0);
    },
    accessList: {
      handler(updatedList, old) {
        this.$set(this, 'accessListItems', [...updatedList]);
        if (updatedList.length != old.length) {
          this.$nextTick(() => {
            this.selectedItems = [];
            this.$refs.accessTable.refreshTable();
          });
        }
        if (!this.readOnlyAccessReview) {
          for (var item of updatedList) {
            if (!this.accessLevels.includes(item.newValue)) {
              this.$emit('update-validation', this.step, false);
              return;
            }
          }
        }
        this.$emit('update-validation', this.step, true);
      },
    },
    resetSelectedAccess: {
      handler() {
        if (this.$refs.accessTable) {
          this.$refs.accessTable.selected = [];
          this.$refs.accessTable.selectedItems = [];
        }
      },
    },
  },
  created() {
    this.accessReviewColumns = {
      Servers: [
        {
          field: 'Name',
          defaultSort: 'asc',
        },
        {
          field: 'newValue',
          title: 'New Value',
          cell: 'keepRetireTemplate',
          invisible: this.readOnlyAccessReview,
        },
        {
          field: 'AccessChangeReason',
          title: 'Access Change Reason',
          filter: 'text',
          cell: 'accessChangeReason',
        },
      ],
      Groups: [
        {
          field: 'Name',
          defaultSort: 'asc',
        },
        {
          field: 'newValue',
          cell: 'keepRetireTemplate',
          invisible: this.readOnlyAccessReview,
        },
        {
          field: 'AccessChangeReason',
          title: 'Access Change Reason',
          filter: 'text',
          cell: 'accessChangeReason',
        },
      ],
      Applications: [
        {
          field: 'Name',
          defaultSort: 'asc',
        },
        {
          field: 'newValue',
          cell: 'keepRetireTemplate',
          invisible: this.readOnlyAccessReview,
        },
        {
          field: 'AccessChangeReason',
          title: 'Access Change Reason',
          filter: 'text',
          cell: 'accessChangeReason',
        },
      ],
      Collections: [
        {
          field: 'Name',
          defaultSort: 'asc',
        },
        {
          field: 'newValue',
          cell: 'keepRetireTemplate',
          invisible: this.readOnlyAccessReview,
        },
        {
          field: 'AccessChangeReason',
          title: 'Access Change Reason',
          filter: 'text',
          cell: 'accessChangeReason',
        },
      ],
      Roles: [
        {
          field: 'name',
          title: 'Name',
          defaultSort: 'asc',
        },
        {
          field: 'newValue',
          title: 'New Value',
          cell: 'keepRetireTemplate',
          invisible: this.readOnlyAccessReview,
        },
        {
          field: 'AccessChangeReason',
          title: 'Access Change Reason',
          filter: 'text',
          cell: 'accessChangeReason',
        },
      ],
      Functions: [
        {
          field: 'Name',
          defaultSort: 'asc',
        },
        {
          field: 'newValue',
          title: 'New Value',
          cell: 'keepRetireTemplate',
          invisible: this.readOnlyAccessReview,
        },
        {
          field: 'AccessChangeReason',
          title: 'Access Change Reason',
          filter: 'text',
          cell: 'accessChangeReason',
        },
      ],
      SharePoint: [
        {
          field: 'name',
          title: 'Name',
          defaultSort: 'asc',
        },
        {
          field: 'newValue',
          title: 'New Value',
          cell: 'keepRetireTemplate',
          invisible: this.readOnlyAccessReview,
        },
        {
          field: 'value',
          title: 'Original Value',
          cell: 'warningTemplate',
        },
        {
          field: 'AccessChangeReason',
          title: 'Access Change Reason',
          filter: 'text',
          cell: 'accessChangeReason',
        },
      ],
      OneDrive: [
        {
          field: 'name',
          title: 'Name',
          defaultSort: 'asc',
        },
        {
          field: 'newValue',
          title: 'New Value',
          cell: 'keepRetireTemplate',
          invisible: this.readOnlyAccessReview,
        },
        {
          field: 'Email',
          title: 'Email',
        },
        {
          field: 'AccessChangeReason',
          title: 'Access Change Reason',
          filter: 'text',
          cell: 'accessChangeReason',
        },
      ],
      Teams: [
        {
          field: 'name',
          title: 'Name',
          defaultSort: 'asc',
        },
        {
          field: 'newValue',
          title: 'New Value',
          cell: 'keepRetireTemplate',
          invisible: this.readOnlyAccessReview,
        },
        {
          field: 'AccessChangeReason',
          title: 'Access Change Reason',
          filter: 'text',
          cell: 'accessChangeReason',
        },
      ],
    };
  },
  methods: {
    keepRetireSelectionChange(item) {
      this.selectedItems = item;
    },
    async addAccessForUsers(userIds) {
      try {
        for (let id of userIds) {
          if (this.accessList.map((a) => a.stsGuid).includes(id)) {
            this.snackbarMessage = 'Selected user already in access list';
            this.snackbar = true;
            continue;
          }
          const result = await fetch(`/ADUser/Info?stsGuid=${id}`);
          this.accessList.push(result[0]);
          let index = this.accessList.length - 1;
          this.accessList[
            index
          ].tooltip = `${result[0].ShortName}\\${result[0].SamAccountName}`;
          this.accessList[index].stsGuid = result[0].STSGuid;
          this.accessList[index].name = result[0].Name;
          this.accessList[index].value = null;
          this.accessList[index].newValue =
            this.assetType.name == 'SharePoint' ? 'Read Only' : 'Keep';
          this.accessList[index].isTextFieldEnabled = true;
          this.accessList[index].addAccessForUsers = true;
        }
      } catch (e) {
        console.error(e);
        this.errorMessage = 'Unable to add users to access list';
      }
      this.$nextTick(() => {
        this.$refs.accessTable.refreshTable();
      });
    },
    updateLoader(isLoad) {
      this.isLoading = isLoad;
      if (this.readOnlyAccessReview) {
        this.$emit('update-validation', this.step, true);
      }
    },
    updateAccess(items, accessLevel) {
      if (accessLevel === items[0].value) {
        items[0].reason = '';
      }
      if (accessLevel !== items[0].value) {
        items[0].reason = '';
        this.$set(items[0], 'isTextFieldEnabled', true);
      } else {
        this.$set(items[0], 'isTextFieldEnabled', false);
      }
      items[0].newValue = accessLevel;
      this.$emit('update-access', items);
    },
    UpdateRemoveReason(item) {
      this.$refs.accessTable.items = this.$refs.accessTable.items.map((i) => {
        if (item[0].ID === i.ID && item[0].STSGuid === i.STSGuid) {
          i.reason = item[0].reason;
        }
        return i;
      });
      this.$emit('update-access', item);
    },
    updateSelectedAccess(selectedItems, accessLevel) {
      if (Array.isArray(selectedItems)) {
        selectedItems.forEach((item) => {
          if (accessLevel === item.value) {
            item.reason = '';
          }
          if (accessLevel !== item.value) {
            this.$set(item, 'isTextFieldEnabled', true);
            this.$set(item, 'reason', '');
          } else {
            this.$set(item, 'isTextFieldEnabled', false);
            this.$set(item, 'reason', '');
          }
          item.newValue = accessLevel;
          this.$refs.accessTable.items = this.$refs.accessTable.items.map(
            (i) => {
              if (item.ID === i.ID) {
                i.newValue = accessLevel;
                if (accessLevel !== item.value) {
                  i.isTextFieldEnabled = true;
                } else {
                  i.isTextFieldEnabled = false;
                }
              }
              return i;
            }
          );
        });
        this.$refs.accessTable.selected = [];
        this.$refs.accessTable.selectedItems = [];
        this.$emit('update-access', selectedItems);
      }
    },
    validateReviewStepAccess() {
      var val = this.accessListItems.some(
        (data) =>
          data.newValue !== data.value &&
          (data.reason === '' || data.reason === undefined)
      );
      if (val) {
        this.validateRules = true;
        this.$refs.accessTable.items = this.$refs.accessTable.items.map((i) => {
          if (i.addAccessForUsers && i.reason == undefined) {
            this.$set(i, 'reason', '');
          }
          return i;
        });
      } else {
        this.$emit('set-validate', false);
        this.$emit('keepOnClick');
      }
    },
  },
};
</script>
<style>
#accessList tfoot tr td {
  padding: 10px 0 0 0;
}

#accessList tfoot tr td > :first-child {
  margin-left: 0;
}
</style>
<style lang="scss">
.access-Table {
  .sphere-table-container {
    position: relative;
  }

  .k-grid-container {
    max-height: Max(calc(100vh - 500px), 200px);
  }
  .k-table-actions {
    .v-btn.success {
      background: #00ce7c !important;
      border-color: #00ce7c !important;
      color: #fff !important;
    }
    .v-btn.error {
      background: #ff4247 !important;
      border-color: #ff4247 !important;
      color: #fff !important;
    }
  }
  .v-btn-toggle {
    .theme--light.v-btn--active {
      background-color: #1976d2;
      color: #fff;
    }
  }
}
</style>
